import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import type { DisruptionPnrGroupRow, NexaPurchaseRecovery } from '@/lib/disruptions/types';
import { pnrGroupScopeKey, type PnrGroupScope } from '@/lib/pnr/scope';
import { newIdempotencyKey } from '@/lib/uuid';
import { recoveryApi, recoveryErrorCode, recoveryKeys } from './api';
import type { IRecoveryBulkResult, IRecoveryView, RecoveryState } from './types';
import { hasRecovery } from './recovery-model';

type RosterPage = { items: DisruptionPnrGroupRow[]; nextCursor?: string | null };

/**
 * The recovery view of ONE group, read once when the card opens and again only when the
 * roster row's recovery changes (the row is the WS-merged truth; its state + purchase
 * stamp are part of the key, so a real change fetches the fresh view and nothing polls).
 */
export function useGroupRecovery(group: DisruptionPnrGroupRow) {
  const purchase = group.nexaPurchase;
  const stamp = `${purchase?.recovery?.state ?? '-'}|${purchase?.updatedAt ?? '-'}`;
  return useQuery<IRecoveryView>({
    queryKey: [...recoveryKeys.view(group.caseUrn, group.subCaseUrn), stamp],
    queryFn: () => recoveryApi.view(group.caseUrn, group.subCaseUrn),
    enabled: hasRecovery(group),
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: Infinity,
  });
}

/** Patch the open scope's roster row with the state a write answered (the denorm is async). */
function patchRow(qc: QueryClient, scope: PnrGroupScope, subCaseUrn: string, state: RecoveryState): void {
  qc.setQueriesData<InfiniteData<RosterPage>>({ queryKey: pnrGroupScopeKey(scope) }, (old) => {
    if (!old?.pages) return old;
    return {
      ...old,
      pages: old.pages.map((p) => ({
        ...p,
        items: p.items.map((g) => {
          if (g.subCaseUrn !== subCaseUrn || !g.nexaPurchase) return g;
          const { recovery: prior, ...purchase } = g.nexaPurchase;
          // `NONE` never rides the row: the recovery is simply gone.
          if (state === 'NONE') return { ...g, nexaPurchase: purchase };
          const recovery: NexaPurchaseRecovery = { ...prior, state };
          return { ...g, nexaPurchase: { ...purchase, recovery } };
        }),
      })),
    };
  });
}

export type RecoveryAction =
  | { kind: 'search' }
  | { kind: 'offer'; optionUrn: string }
  | { kind: 'buy'; optionUrn: string }
  | { kind: 'reconcile' }
  | { kind: 'giveUp' }
  | { kind: 'noFlight' };

function run(action: RecoveryAction, caseUrn: string, groupId: string, key: string): Promise<IRecoveryView> {
  switch (action.kind) {
    case 'search':
      return recoveryApi.search(caseUrn, groupId, key);
    case 'offer':
      return recoveryApi.offer(caseUrn, groupId, action.optionUrn, key);
    case 'buy':
      return recoveryApi.buy(caseUrn, groupId, action.optionUrn, key);
    case 'reconcile':
      return recoveryApi.reconcile(caseUrn, groupId, key);
    case 'giveUp':
      return recoveryApi.giveUpUnconfirmed(caseUrn, groupId, key);
    case 'noFlight':
      return recoveryApi.noFlight(caseUrn, groupId, key);
  }
}

/**
 * Every per-group recovery write (§6.6) through one mutation: the answer replaces the
 * cached view and the roster row's state; the WS brings the authoritative row after.
 * One Idempotency-Key per operator intent — the button is disabled while it runs.
 */
export function useRecoveryAction(scope: PnrGroupScope, group: DisruptionPnrGroupRow) {
  const qc = useQueryClient();
  return useMutation<IRecoveryView, Error, RecoveryAction>({
    mutationKey: ['recovery-action', group.caseUrn, group.subCaseUrn],
    mutationFn: (action) => run(action, group.caseUrn, group.subCaseUrn, newIdempotencyKey()),
    onSuccess: (view) => {
      qc.setQueriesData<IRecoveryView>(
        { queryKey: recoveryKeys.view(group.caseUrn, group.subCaseUrn) },
        () => view,
      );
      patchRow(qc, scope, group.subCaseUrn, view.state);
    },
    onError: (error) => {
      // The options moved under the click (no room left, not a candidate any more): read them again.
      const code = recoveryErrorCode(error);
      if (code === 'NO_CAPACITY' || code === 'TOO_SOON' || code === 'MOVED') {
        void qc.invalidateQueries({ queryKey: recoveryKeys.view(group.caseUrn, group.subCaseUrn) });
      }
    },
  });
}

/** Bulk recovery for a set of groups of one disruption («Te necesitan», slice OU2). */
export function useBulkRecovery(disruptionUrn: string) {
  return useMutation<IRecoveryBulkResult, Error, { kind: 'search' | 'offerBest'; subCaseUrns: string[] }>({
    mutationKey: ['recovery-bulk', disruptionUrn],
    mutationFn: ({ kind, subCaseUrns }) =>
      kind === 'search'
        ? recoveryApi.bulkSearch(disruptionUrn, subCaseUrns)
        : recoveryApi.bulkOfferBest(disruptionUrn, subCaseUrns),
  });
}
